import { useReactFlow } from '@xyflow/react';
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { parseTourSteps, stepDurationMs, type TourFocus, type TourStep } from '../../lib/tour';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import Markdown, { type ReferenceHandler } from '../workspace/Markdown';

interface Props {
  /** The diagram's explanation (Markdown) containing the numbered "Step N:" sections. */
  documentation: string;
  /** Which canvas items (and connectors) a step lights up. Re-evaluated whenever `focusKey` changes. */
  focusFor: (step: TourStep) => TourFocus;
  /** Changes whenever the diagram itself changes, so the lit-up parts are recomputed. */
  focusKey: unknown;
  /** Clicking a part named in the caption: the canvas ids to zoom to, or null if it is not found. */
  resolveReference: (kind: string, target: string) => string[] | null;
  /** Shown when the explanation has no steps. */
  emptyHint: ReactNode;
}

/**
 * The guided "story" of a diagram. It reads the numbered steps written in the diagram's explanation ("Step 1: ...")
 * and plays them like a tour: the camera flies to the parts each step names, those parts light up while everything
 * else fades back, and a caption narrates. It can play on its own or be driven step by step. Used for both
 * architecture and database diagrams; only what a step lights up differs.
 */
export default function TourPlayer({ documentation, focusFor, focusKey, resolveReference, emptyHint }: Props) {
  const start = useDiagramViewStore((s) => s.tourStart);
  const closeTour = useDiagramViewStore((s) => s.closeTour);
  const setTourFocus = useDiagramViewStore((s) => s.setTourFocus);
  const reactFlow = useReactFlow();

  const steps = useMemo(() => parseTourSteps(documentation), [documentation]);
  const [index, setIndex] = useState(() => Math.min(Math.max(start, 0), Math.max(steps.length - 1, 0)));
  const [playing, setPlaying] = useState(true);
  const [finished, setFinished] = useState(false);
  const step = steps[index];
  const cardRef = useRef<HTMLDivElement>(null);

  // light up this step's parts and fly the camera to them (leaving room for the caption underneath)
  useEffect(() => {
    if (!step) return;
    const focus = focusFor(step);
    setTourFocus(focus);
    const ids = [...focus.nodeIds];
    if (ids.length) {
      const cardHeight = (cardRef.current?.offsetHeight ?? 260) + 40;
      reactFlow.fitView({
        nodes: ids.map((id) => ({ id })),
        padding: { top: '90px', right: '90px', bottom: `${cardHeight}px`, left: '90px' },
        maxZoom: 1.15,
        duration: 900,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, focusKey, reactFlow, setTourFocus]);

  // leaving the tour puts everything back to normal
  useEffect(() => () => setTourFocus(null), [setTourFocus]);

  // auto-advance while playing
  useEffect(() => {
    if (!playing || !step) return;
    const timer = window.setTimeout(() => {
      if (index < steps.length - 1) setIndex(index + 1);
      else {
        setPlaying(false);
        setFinished(true);
      }
    }, stepDurationMs(step));
    return () => window.clearTimeout(timer);
  }, [playing, index, step, steps.length]);

  const go = (next: number) => {
    setFinished(false);
    setIndex(Math.min(Math.max(next, 0), steps.length - 1));
  };
  const togglePlay = () => {
    if (finished) {
      go(0);
      setPlaying(true);
    } else setPlaying((p) => !p);
  };

  // keyboard: space plays/pauses, arrows step, escape leaves
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.key === 'Escape') closeTour();
      else if (e.key === ' ') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'ArrowRight') go(index + 1);
      else if (e.key === 'ArrowLeft') go(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // clicking a part named in the caption pauses the tour and shows that part up close
  const handleReference: ReferenceHandler = (kind, target) => {
    const ids = resolveReference(kind, target);
    if (!ids?.length) return;
    setPlaying(false);
    reactFlow.fitView({ nodes: ids.map((id) => ({ id })), padding: { top: '120px', right: '120px', bottom: '320px', left: '120px' }, maxZoom: 1.5, duration: 700 });
  };

  if (!step) {
    return (
      <div className="absolute bottom-4 left-1/2 z-20 w-[min(560px,calc(100%-2rem))] -translate-x-1/2 rounded-2xl border border-gray-200/80 bg-panel p-4 text-sm shadow-pop dark:border-white/10 dark:bg-gray-900 dark:text-gray-200">
        <div className="mb-1 flex items-center justify-between font-semibold">
          This diagram has no story yet
          <button onClick={closeTour} className="btn-ghost !p-1" title="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {emptyHint}
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className="absolute bottom-4 left-1/2 z-20 w-[min(700px,calc(100%-2rem))] -translate-x-1/2 overflow-hidden rounded-2xl border border-gray-200/80 bg-panel/95 shadow-pop backdrop-blur dark:border-white/10 dark:bg-gray-900/95"
    >
      <div className="h-1 bg-gray-100 dark:bg-white/10">
        {/* restarts for every step; freezes when paused */}
        <div
          key={`${index}-${finished}`}
          className="tour-progress h-full bg-sky-500"
          style={{ animationDuration: `${stepDurationMs(step)}ms`, animationPlayState: playing ? 'running' : 'paused', width: finished ? '100%' : undefined }}
        />
      </div>
      <div className="p-4 pb-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-400">
            The story · step {index + 1} of {steps.length}
          </span>
          <button onClick={closeTour} className="btn-ghost !p-1" title="Leave the story (Esc)">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mb-1 text-lg font-semibold tracking-tight dark:text-gray-100">{step.title}</div>
        <div className="max-h-[26vh] overflow-y-auto pr-1">
          <Markdown source={step.markdown} onReference={handleReference} />
        </div>
      </div>
      <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 dark:border-white/10">
        <button onClick={() => go(index - 1)} disabled={index === 0} className="btn-ghost !px-2 !py-1.5 disabled:opacity-30" title="Previous step (←)">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button onClick={togglePlay} className="btn-primary !px-3 !py-1.5" title="Play or pause (Space)">
          {finished ? <RotateCcw className="h-4 w-4" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {finished ? 'Replay' : playing ? 'Pause' : 'Play'}
        </button>
        <button onClick={() => go(index + 1)} disabled={index === steps.length - 1} className="btn-ghost !px-2 !py-1.5 disabled:opacity-30" title="Next step (→)">
          <ChevronRight className="h-4 w-4" />
        </button>
        <div className="ml-2 flex flex-1 flex-wrap items-center justify-end gap-1">
          {steps.map((s) => (
            <button
              key={s.index}
              onClick={() => go(s.index)}
              title={`Step ${s.index + 1}: ${s.title}`}
              className={`h-2 rounded-full transition-all ${s.index === index ? 'w-5 bg-sky-500' : s.index < index ? 'w-2 bg-sky-300 dark:bg-sky-700' : 'w-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
